import React from "react";
import { navigate } from "gatsby";
import Section from "../../../styles/Section.js";
import { Highlight } from "../../../styles/Utilities.js";
import { H2, H3 } from "../../../styles/Headings.js";
import { P2 } from "../../../styles/Paragraphs.js";
import Button, { IconButton } from "../../../styles/Button.js";
import Tag from "../../../styles/Tag.js";
import Image from "../../../components/Image.js";
import { FaGithubAlt } from "react-icons/fa";
import { BiLinkExternal } from "react-icons/bi";
import {
   Projects,
   Project,
   ProjectMain,
   ProjectNum,
   ProjectTags,
   ProjectActions,
   ProjectImg,
   ImagesGroup
} from "./ProjectsSectionStyles.js";
import { motion } from "framer-motion";
import InView from "react-intersection-observer";

const projectImageVariant = {
   hidden: {
      y: 50,
      opacity: 0,
      transition: {
         y: {
            duration: 0.6,
            type: "spring",
            stiffness: 85
         },
         opacity: {
            duration: 0.6
         }
      }
   },
   visible: i => ({
      y: 0,
      opacity: 1,
      transition: {
         y: {
            type: "spring",
            stiffness: 85,
            duration: 0.6,
            delay: i
         },
         opacity: {
            duration: 0.6,
            delay: i
         }
      }
   })
};

function ProjectsSectionContainer() {
   const handleCaseStudy = url => () => {
      navigate(url);
   };

   const handleUrl = url => () => {
      window.open(url, "_blank");
   };

   return (
      <Section id="projects">
         <H3 center>
            <Highlight>My</Highlight> Projects
         </H3>
         <Projects>
            <Project>
               <ProjectMain>
                  <ProjectNum>01.</ProjectNum>
                  <H2>
                     Fanfic <Highlight>Arena</Highlight>
                  </H2>
                  <P2 style={{ margin: "0.6rem 0 1.4rem" }}>
                     An online platform for people who love to read, write and
                     explore fan fictions. Browse through a variety of
                     categories of fictions and read what you love.
                  </P2>
                  <ProjectTags style={{ marginBottom: "2.6rem" }}>
                     <Tag>Node.js</Tag>
                     <Tag>MongoDB</Tag>
                     <Tag>Express.js</Tag>
                     <Tag>React.js</Tag>
                     <Tag>Redux</Tag>
                  </ProjectTags>
                  <ProjectActions>
                     <Button onClick={handleCaseStudy("/projects/fanficarena")}>
                        Case Study
                     </Button>
                     <IconButton
                        inverse
                        onClick={handleUrl(
                           "https://github.com/paras594/fanfic-arena"
                        )}
                        style={{ margin: "0 0.8rem" }}
                     >
                        <FaGithubAlt />
                     </IconButton>
                     <IconButton
                        inverse
                        onClick={handleUrl(
                           "https://fanfic-arena.herokuapp.com/"
                        )}
                     >
                        <BiLinkExternal />
                     </IconButton>
                  </ProjectActions>
               </ProjectMain>
               <ProjectImg>
                  <InView threshold={0.5}>
                     {({ ref, inView }) => (
                        <ImagesGroup ref={ref}>
                           <motion.div
                              custom={0}
                              initial="hidden"
                              animate={inView ? "visible" : ""}
                              variants={projectImageVariant}
                           >
                              <Image
                                 className="desktop"
                                 src="fanfic-arena-homepage.png"
                                 alt="fanfic arena"
                              />
                           </motion.div>
                           <motion.div
                              custom={0.3}
                              initial="hidden"
                              animate={inView ? "visible" : ""}
                              variants={projectImageVariant}
                           >
                              <Image
                                 className="mobile"
                                 src="fanfic-arena-mobile.png"
                                 alt="fanfic arena mobile"
                              />
                           </motion.div>
                        </ImagesGroup>
                     )}
                  </InView>
               </ProjectImg>
            </Project>
            <Project>
               <ProjectMain>
                  <ProjectNum>02.</ProjectNum>
                  <H2>
                     Hotel <Highlight>Montserrat</Highlight>
                  </H2>
                  <P2 style={{ margin: "0.6rem 0 1.4rem" }}>
                     A website for an imaginary hotel named 'Hotel Montserrat',
                     featuring qualities and services offered by the hotel.
                     Customers can see different types rooms and services
                     available at hotel.
                  </P2>
                  <ProjectTags style={{ marginBottom: "2.6rem" }}>
                     <Tag>HTML</Tag>
                     <Tag>CSS</Tag>
                     <Tag>Javascript</Tag>
                     <Tag>Webpack</Tag>
                     <Tag>NPM</Tag>
                  </ProjectTags>
                  <ProjectActions>
                     <Button
                        onClick={handleCaseStudy("/projects/hotelmontserrat")}
                     >
                        Case Study
                     </Button>
                     <IconButton
                        inverse
                        onClick={handleUrl(
                           "https://github.com/paras594/Montserrat-Hotel-Template"
                        )}
                        style={{ margin: "0 0.8rem" }}
                     >
                        <FaGithubAlt />
                     </IconButton>
                     <IconButton
                        inverse
                        onClick={handleUrl(
                           "https://montserrat-hotel-template.netlify.app/"
                        )}
                     >
                        <BiLinkExternal />
                     </IconButton>
                  </ProjectActions>
               </ProjectMain>
               <ProjectImg>
                  <InView threshold={0.5}>
                     {({ ref, inView }) => (
                        <ImagesGroup ref={ref}>
                           <motion.div
                              custom={0}
                              initial="hidden"
                              animate={inView ? "visible" : ""}
                              variants={projectImageVariant}
                           >
                              <Image
                                 className="desktop"
                                 src="hotel-montserrat-homepage.png"
                                 alt="Hotel Montserrat"
                              />
                           </motion.div>
                           <motion.div
                              custom={0.3}
                              initial="hidden"
                              animate={inView ? "visible" : ""}
                              variants={projectImageVariant}
                           >
                              <Image
                                 className="mobile"
                                 src="hotel-montserrat-mobile.png"
                                 alt="Hotel Montserrat mobile"
                              />
                           </motion.div>
                        </ImagesGroup>
                     )}
                  </InView>
               </ProjectImg>
            </Project>
            <Project>
               <ProjectMain>
                  <ProjectNum>03.</ProjectNum>
                  <H2>
                     URL <Highlight>Shortner</Highlight>
                  </H2>
                  <P2 style={{ margin: "0.6rem 0 1.4rem" }}>
                     A fast and simple online tool for converting long, ugly
                     URLs and links into more manageable short URLs.
                  </P2>
                  <ProjectTags style={{ marginBottom: "2.6rem" }}>
                     <Tag>Node.js</Tag>
                     <Tag>MongoDB</Tag>
                     <Tag>EJS</Tag>
                     <Tag>Bootstrap</Tag>
                     <Tag>AJAX</Tag>
                  </ProjectTags>
                  <ProjectActions>
                     <Button
                        onClick={handleCaseStudy("/projects/urlshortener")}
                     >
                        Case Study
                     </Button>
                     <IconButton
                        inverse
                        onClick={handleUrl(
                           "https://github.com/paras594/url-shortner"
                        )}
                        style={{ margin: "0 0.8rem" }}
                     >
                        <FaGithubAlt />
                     </IconButton>
                     <IconButton
                        inverse
                        onClick={handleUrl("https://squeezeurl.herokuapp.com/")}
                     >
                        <BiLinkExternal />
                     </IconButton>
                  </ProjectActions>
               </ProjectMain>
               <ProjectImg>
                  <InView threshold={0.5}>
                     {({ ref, inView }) => (
                        <ImagesGroup ref={ref}>
                           <motion.div
                              custom={0}
                              initial="hidden"
                              animate={inView ? "visible" : ""}
                              variants={projectImageVariant}
                           >
                              <Image
                                 className="desktop url-shortner-desktop"
                                 src="url-shortner-homepage.png"
                                 alt="Hotel Montserrat"
                              />
                           </motion.div>
                           <motion.div
                              custom={0.3}
                              initial="hidden"
                              animate={inView ? "visible" : ""}
                              variants={projectImageVariant}
                           >
                              <Image
                                 className="mobile url-shortner-mobile"
                                 src="url-shortner-mobile.png"
                                 alt="Url Shortner mobile"
                              />
                           </motion.div>
                        </ImagesGroup>
                     )}
                  </InView>
               </ProjectImg>
            </Project>
         </Projects>
      </Section>
   );
}

export default ProjectsSectionContainer;
